import { useEffect, useState } from "react";
import { Calendar, MapPin, Users, Package, Coins, Tag, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { CategoryIcon } from "@/components/CategoryIcon";
import { playTap } from "@/lib/sounds";

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon size={18} className="ga-muted mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="ga-muted text-[11px] uppercase tracking-wide">{label}</div>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmMemory({ analysis, onSave, onBack, saving }) {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(analysis);
  const [categories, setCategories] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data.map((c) => c.name))); }, []);
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const setList = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }));
  const today = new Date().toISOString().slice(0, 10);

  if (editing) {
    return (
      <div className="ga-fade-up space-y-3 px-5" data-testid="confirm-memory-edit-form">
        <label className="block text-xs font-medium ga-muted">Título<input className="ga-input mt-1" value={data.title || ""} onChange={set("title")} data-testid="edit-title-input" /></label>
        <label className="block text-xs font-medium ga-muted">Resumo<textarea className="ga-input mt-1" rows={2} value={data.summary || ""} onChange={set("summary")} data-testid="edit-summary-input" /></label>
        <label className="block text-xs font-medium ga-muted">Categoria
          <select className="ga-input mt-1" value={data.category} onChange={set("category")} data-testid="edit-category-select">
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium ga-muted">Local<input className="ga-input mt-1" value={data.location || ""} onChange={set("location")} data-testid="edit-location-input" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium ga-muted">Data<input type="date" className="ga-input mt-1" value={data.event_date || ""} onChange={set("event_date")} data-testid="edit-date-input" /></label>
          <label className="block text-xs font-medium ga-muted">Valor (R$)<input type="number" step="0.01" className="ga-input mt-1" value={data.value ?? ""} onChange={(e) => setData((d) => ({ ...d, value: e.target.value === "" ? null : Number(e.target.value) }))} data-testid="edit-value-input" /></label>
        </div>
        <label className="block text-xs font-medium ga-muted">Pessoas (separe por vírgula)<input className="ga-input mt-1" defaultValue={(data.people || []).join(", ")} onChange={setList("people")} data-testid="edit-people-input" /></label>
        <label className="block text-xs font-medium ga-muted">Objetos<input className="ga-input mt-1" defaultValue={(data.objects || []).join(", ")} onChange={setList("objects")} data-testid="edit-objects-input" /></label>
        <label className="block text-xs font-medium ga-muted">Palavras-chave<input className="ga-input mt-1" defaultValue={(data.keywords || []).join(", ")} onChange={setList("keywords")} data-testid="edit-keywords-input" /></label>
        <div className="flex gap-3 pt-2">
          <button onClick={() => setEditing(false)} className="ga-btn-ghost flex-1" data-testid="edit-done-button">Pronto</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-fade-up px-5" data-testid="confirm-memory-screen">
      <div className="ga-card p-5">
        <div className="flex items-center gap-3">
          <CategoryIcon category={data.category} size={52} iconSize={24} />
          <div className="min-w-0">
            <div className="ga-muted text-[11px] uppercase tracking-wide">Título</div>
            <h2 className="truncate text-lg font-semibold" data-testid="confirm-title">{data.title}</h2>
          </div>
        </div>
        <p className="mt-3 text-sm" data-testid="confirm-summary">{data.summary}</p>
        <div className="mt-3 divide-y" style={{ borderColor: "var(--ga-border)" }}>
          <Row icon={Tag} label="Categoria"><span data-testid="confirm-category">{data.category}</span></Row>
          {data.location && <Row icon={MapPin} label="Local">{data.location}</Row>}
          <Row icon={Calendar} label="Data">{!data.event_date || data.event_date === today ? "Hoje" : shortDate(data.event_date)}</Row>
          {data.people?.length > 0 && <Row icon={Users} label="Pessoas">{data.people.join(", ")}</Row>}
          {data.objects?.length > 0 && <Row icon={Package} label="Objetos">{data.objects.join(", ")}</Row>}
          {data.value != null && <Row icon={Coins} label="Valor">R$ {Number(data.value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</Row>}
          {data.loan?.is_loan && <Row icon={Sparkles} label="Empréstimo">{data.loan.direction === "borrowed" ? "Peguei emprestado de" : "Emprestei para"} {data.loan.person} • Status: emprestado</Row>}
          {data.document && Object.values(data.document).some(Boolean) && (
            <Row icon={Sparkles} label="Documento">
              {Object.entries({ product: "Produto", store: "Loja", purchase_date: "Data", warranty_until: "Validade", value: "Valor", model: "Modelo", document_number: "Nº documento" })
                .filter(([k]) => data.document[k]).map(([k, l]) => <div key={k}><span className="ga-muted">{l}:</span> {String(data.document[k])}</div>)}
            </Row>
          )}
        </div>
      </div>
      <p className="mt-6 text-center text-base font-medium">Quer salvar assim?</p>
      <div className="mt-3 flex gap-3">
        <button onClick={() => setEditing(true)} className="ga-btn-ghost flex-1 uppercase" data-testid="confirm-edit-button">Editar</button>
        <button onClick={() => { playTap(); onSave(data); }} disabled={saving} className="ga-btn-primary flex-[1.4] uppercase" data-testid="confirm-save-button">{saving ? "Guardando..." : "Salvar"}</button>
      </div>
      <button onClick={onBack} className="ga-muted mt-4 w-full text-center text-sm" data-testid="confirm-back-button">Voltar e alterar o registro</button>
    </div>
  );
}
